// src/modules/Projects/SampleRequestPage.js
// VERSION: 1.1 - Sample Request Management (with Print)
// Allows creating sample requests from development projects ready to be sent

import React, { useState, useMemo, useCallback, useEffect, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, getDocs, collection, addDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import useAuth from '../../hooks/useAuth';
import styles from './Project.module.css';

// Notification imports
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiPackage,
    FiPlus,
    FiSearch,
    FiCalendar,
    FiBox,
    FiTruck,
    FiFileText,
    FiEye,
    FiClipboard,
    FiUser,
    FiHome,
    FiChevronUp,
    FiChevronDown,
    FiPrinter
} from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';
import { FaFlask, FaShip } from 'react-icons/fa';

// ============================================
// 1. CONSTANTS AND DEFINITIONS
// ============================================
const SAMPLE_REQUESTS_COLLECTION = 'sample-requests';
const PROJECTS_COLLECTION = 'development-projects';
const CLIENTS_COLLECTION = 'clients';
const ITEMS_PER_PAGE = 10;
const LOADING_TIMEOUT = 8000;

const REQUEST_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    SENT: 'sent',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled'
};

const REQUEST_STATUS_LABELS = {
    [REQUEST_STATUS.DRAFT]: { label: 'Rascunho', color: '#6c757d', bg: '#e9ecef' },
    [REQUEST_STATUS.PENDING]: { label: 'Pendente', color: '#856404', bg: '#fff3cd' },
    [REQUEST_STATUS.APPROVED]: { label: 'Aprovado', color: '#155724', bg: '#d4edda' },
    [REQUEST_STATUS.SENT]: { label: 'Enviado', color: '#0c5460', bg: '#d1ecf1' },
    [REQUEST_STATUS.DELIVERED]: { label: 'Entregue', color: '#0c5460', bg: '#d1ecf1' },
    [REQUEST_STATUS.CANCELLED]: { label: 'Cancelado', color: '#721c24', bg: '#f8d7da' }
};

const SHIPPING_METHODS = [
    { value: 'sea', label: 'Marítimo (Sea)', icon: '🚢' },
    { value: 'air', label: 'Aéreo (Air)', icon: '✈️' },
    { value: 'courier', label: 'Courier', icon: '📦' }
];

const DEFAULT_REQUEST_MODEL = {
    code: '',
    clientId: '',
    clientName: '',
    requestDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    shippingMethod: 'sea',
    carrier: '',
    trackingCode: '',
    notes: '',
    items: [],
    status: REQUEST_STATUS.DRAFT,
    createdAt: null,
    updatedAt: null,
    createdBy: ''
};

// ============================================
// 2. AUXILIARY COMPONENTS
// ============================================

// Loading Overlay
const LoadingOverlay = ({ onNavigateHome }) => {
    const { t } = useTranslation();

    return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <h3>Carregando solicitações de amostras...</h3>
                <p>Aguarde enquanto buscamos os dados.</p>
                <button onClick={onNavigateHome} className={styles.navigationButton}>
                    <FiHome size={18} />
                    Ir para Dashboard
                </button>
            </div>
        </div>
    );
};

// Status Badge
const RequestStatusBadge = ({ status }) => {
    const config = REQUEST_STATUS_LABELS[status] || REQUEST_STATUS_LABELS[REQUEST_STATUS.DRAFT];
    return (
        <span
            className={styles.statusBadge}
            style={{
                background: config.bg,
                color: config.color,
                borderLeft: `3px solid ${config.color}`
            }}
        >
            {config.label}
        </span>
    );
};

// Form Section Component
const FormSection = ({ title, icon, children, required = false, badge }) => (
    <div className={styles.card}>
        <div className={styles.cardHeader}>
            <span className={styles.cardIcon}>{icon}</span>
            <h3 className={styles.cardTitle}>{title}</h3>
            {required && <span className={styles.sectionBadge}>Obrigatório</span>}
            {badge && <span className={styles.sectionBadge}>{badge}</span>}
        </div>
        <div className={styles.cardBody}>
            {children}
        </div>
    </div>
);

// ============================================
// 3. MAIN COMPONENT
// ============================================
const SampleRequestPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('requisicao_de_amostra');
    const { logActivity } = useActivityLogger();
    const { addNotification } = useNotifications();
    const currentUser = useAuth();

    // --- Transition for smooth UI updates ---
    const [isPending, startTransition] = useTransition();

    // --- Modal States ---
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    // --- Pagination and Filter States ---
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // --- Loading timeout state ---
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const loadingTimeoutRef = React.useRef(null);

    // --- Form States ---
    const [selectedClientId, setSelectedClientId] = useState('');
    const [clientName, setClientName] = useState('');
    const [requestDate, setRequestDate] = useState(new Date().toISOString().split('T')[0]);
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [shippingMethod, setShippingMethod] = useState('sea');
    const [carrier, setCarrier] = useState('');
    const [trackingCode, setTrackingCode] = useState('');
    const [notes, setNotes] = useState('');
    const [requestItems, setRequestItems] = useState([]);

    // --- Item being added ---
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [newItemQuantity, setNewItemQuantity] = useState('1');
    const [newItemUnitPrice, setNewItemUnitPrice] = useState('0');
    const [newItemWeight, setNewItemWeight] = useState('');
    const [newItemWeightUnit, setNewItemWeightUnit] = useState('g');
    const [newItemNotes, setNewItemNotes] = useState('');

    // --- Data Hooks ---
    const {
        data: sampleRequests,
        loading: requestsLoading,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData: refreshRequests
    } = useFirestore(SAMPLE_REQUESTS_COLLECTION, 'createdAt', true);

    const { data: projects, loading: projectsLoading } = useFirestore(PROJECTS_COLLECTION, 'code');
    const { data: clients, loading: clientsLoading } = useFirestore(CLIENTS_COLLECTION, 'razaoSocial');

    // --- Toast System ---
    const addToast = useCallback((message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    }, []);

    // --- Loading timeout handler ---
    useEffect(() => {
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        if (requestsLoading && !sampleRequests?.length) {
            loadingTimeoutRef.current = setTimeout(() => {
                console.warn('⚠️ Loading timeout - allowing navigation');
                setLoadingTimeout(true);
            }, LOADING_TIMEOUT);
        } else {
            setLoadingTimeout(false);
        }

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [requestsLoading, sampleRequests]);

    // ============================================
    // 3.1 COMPUTED DATA
    // ============================================

    // Get projects that are ready to be sent to client
    const readyToSendProjects = useMemo(() => {
        if (!projects) return [];
        return projects.filter(project => {
            const steps = project.steps || {};
            
            // Quality must be completed
            const qualityCompleted = steps.quality?.status === 'completed';
            
            // Client step must NOT be completed (not yet sent)
            const clientNotSent = !steps.client || 
                (steps.client.status !== 'completed' && steps.client.status !== 'sent');
            
            // Project must not be cancelled
            const notCancelled = project.situation !== 'Cancelled';
            
            return qualityCompleted && clientNotSent && notCancelled;
        });
    }, [projects]);

    // Client options - only clients with ready-to-send projects
    const clientOptions = useMemo(() => {
        if (!clients || !readyToSendProjects) return [];
        
        const clientIdsWithProjects = new Set(readyToSendProjects.map(p => p.clientId).filter(Boolean));
        
        return clients
            .filter(client => clientIdsWithProjects.has(client.id))
            .map(client => ({
                id: client.id,
                value: client.id,
                label: `${client.codigo || ''} - ${client.razaoSocial}`,
                razaoSocial: client.razaoSocial
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clients, readyToSendProjects]);

    // Projects available for selected client (not already in request)
    const availableProjectsForClient = useMemo(() => {
        if (!selectedClientId || !readyToSendProjects) return [];
        
        const projectsInRequest = requestItems.map(item => item.projectId);
        
        return readyToSendProjects
            .filter(project => 
                project.clientId === selectedClientId && 
                !projectsInRequest.includes(project.id)
            );
    }, [selectedClientId, readyToSendProjects, requestItems]);

    // Selected project details
    const selectedProject = useMemo(() => {
        if (!selectedProjectId || !projects) return null;
        return projects.find(p => p.id === selectedProjectId);
    }, [selectedProjectId, projects]);

    // Auto-fill weight when project is selected
    useEffect(() => {
        if (selectedProject) {
            const projectWeight = selectedProject.netWeight || 
                selectedProject.grossWeight || 
                selectedProject.briefing?.netWeight || 
                '';
            setNewItemWeight(projectWeight.toString());
        }
    }, [selectedProject]);

    // ============================================
    // 3.2 NOTIFICATION FUNCTIONS
    // ============================================

    const notifySampleRequest = useCallback(async (requestData, requestId, isEditing) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];
                const permissions = Array.isArray(userData.permissions) ? userData.permissions : [];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Gerente de Projetos') ||
                    permissions.includes('projeto')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            console.log(`📢 Enviando notificações de solicitação de amostra para ${targetUsers.length} usuários`);

            const config = getModuleConfig('projeto');

            const baseNotification = {
                type: isEditing ? 'sample_request_updated' : 'sample_request_created',
                module: 'projeto',
                moduleIcon: config?.icon || '🧪',
                moduleColor: config?.color || '#6f42c1',
                moduleName: config?.name || 'Projetos',
                title: isEditing ? '🔄 Solicitação de Amostra Atualizada' : '📦 Nova Solicitação de Amostra',
                fullMessage: `Detalhes:\n• Código: ${requestData.code}\n• Cliente: ${requestData.clientName}\n• Itens: ${requestData.items?.length || 0}\n• Data: ${requestData.requestDate}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'Média',
                route: `/projeto/requisicao-de-amostra`,
                data: {
                    requestId: requestId,
                    requestCode: requestData.code,
                    clientName: requestData.clientName,
                    itemCount: requestData.items?.length || 0
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                addNotification({
                    ...baseNotification,
                    id: `sample_req_${isEditing ? 'updated' : 'created'}_${requestId}_${user.uid}_${Date.now()}`,
                    message: isEditing
                        ? `${user.nome || 'Usuário'}, a solicitação de amostra ${requestData.code} foi atualizada por ${currentUser?.nome || 'Alguém'}`
                        : `${user.nome || 'Usuário'}, nova solicitação de amostra criada: ${requestData.code} para ${requestData.clientName}`,
                    userId: user.uid
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...baseNotification,
                        id: `sample_req_${isEditing ? 'updated' : 'created'}_${requestId}_${user.uid}_${Date.now()}`,
                        message: isEditing
                            ? `${user.nome || 'Usuário'}, a solicitação de amostra ${requestData.code} foi atualizada por ${currentUser?.nome || 'Alguém'}`
                            : `${user.nome || 'Usuário'}, nova solicitação de amostra criada: ${requestData.code} para ${requestData.clientName}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao enviar notificações:', error);
        }
    }, [currentUser, addNotification]);

    // ============================================
    // 3.3 AUXILIARY FUNCTIONS
    // ============================================

    const generateNextCode = useCallback(() => {
        const prefix = 'AM';
        const currentYear = new Date().getFullYear();

        if (!sampleRequests || sampleRequests.length === 0) {
            return `${prefix}001.${currentYear}`;
        }

        const currentYearRequests = sampleRequests.filter(r => {
            if (!r.code) return false;
            return r.code.endsWith(`.${currentYear}`);
        });

        if (currentYearRequests.length === 0) {
            return `${prefix}001.${currentYear}`;
        }

        const numbers = currentYearRequests
            .map(r => {
                const match = r.code.match(/^AM(\d+)\.\d{4}$/);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter(num => num !== null && !isNaN(num));

        let nextNumber = 1;
        if (numbers.length > 0) {
            nextNumber = Math.max(...numbers) + 1;
        }
        return `${prefix}${nextNumber.toString().padStart(3, '0')}.${currentYear}`;
    }, [sampleRequests]);

    const resetForm = useCallback(() => {
        setEditingItem(null);
        setSelectedClientId('');
        setClientName('');
        setRequestDate(new Date().toISOString().split('T')[0]);
        setExpectedDeliveryDate('');
        setShippingMethod('sea');
        setCarrier('');
        setTrackingCode('');
        setNotes('');
        setRequestItems([]);
        setSelectedProjectId('');
        setNewItemQuantity('1');
        setNewItemUnitPrice('0');
        setNewItemWeight('');
        setNewItemWeightUnit('g');
        setNewItemNotes('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const closeModal = useCallback(() => {
        resetForm();
        setIsModalOpen(false);
        setIsSubmitting(false);
    }, [resetForm]);

    // ============================================
    // 3.4 PRINT FUNCTION
    // ============================================

    const handlePrintRequest = useCallback((request) => {
        if (!request) return;

        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            addToast('Por favor, permita pop-ups para imprimir.', 'Warning');
            return;
        }

        const escapeHtml = (str) => String(str ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const itemsRows = (request.items || []).map((item, idx) => `
            <tr>
                <td style="border:1px solid #ddd;padding:6px;text-align:center;">${idx + 1}</td>
                <td style="border:1px solid #ddd;padding:6px;"><strong>${escapeHtml(item.projectCode)}</strong></td>
                <td style="border:1px solid #ddd;padding:6px;">${escapeHtml(item.projectName)}</td>
                <td style="border:1px solid #ddd;padding:6px;">${escapeHtml(item.productType || '-')}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:center;">${item.quantity || 0}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:right;">R$ ${(item.unitPrice || 0).toFixed(2)}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:center;">${(item.weight || 0).toFixed(2)} ${escapeHtml(item.weightUnit || 'g')}</td>
                <td style="border:1px solid #ddd;padding:6px;text-align:right;">R$ ${(item.total || 0).toFixed(2)}</td>
            </tr>
        `).join('');

        const totalQuantity = (request.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
        const totalWeight = (request.items || []).reduce((s, i) => s + (i.weight || 0), 0);
        const totalValue = (request.items || []).reduce((s, i) => s + (i.total || 0), 0);
        const statusLabel = REQUEST_STATUS_LABELS[request.status]?.label || request.status;
        const shippingLabel = SHIPPING_METHODS.find(m => m.value === request.shippingMethod);

        printWindow.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Solicitação de Amostra - ${escapeHtml(request.code)}</title>
  <style>
    @page { size: A4 portrait; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { font-family: Calibri, Arial, sans-serif; font-size: 11px; color: #000; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    th { background: #e8eaf6; border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold; }
    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #1a237e; padding-bottom: 12px; margin-bottom: 16px; }
    .title { font-size: 22px; font-weight: bold; color: #1a237e; letter-spacing: 1px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; padding: 12px; background: #fafafa; border: 1px solid #ddd; }
    .info-item label { font-size: 9px; color: #666; font-weight: bold; display: block; margin-bottom: 2px; }
    .info-item span { font-size: 11px; }
    .totals { font-weight: bold; background: #e8eaf6; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 9px; color: #666; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">SOLICITAÇÃO DE AMOSTRA</div>
      <div style="font-size:12px;font-weight:bold;margin-top:4px;">Código: ${escapeHtml(request.code)}</div>
    </div>
    <div style="text-align:right;font-size:10px;">
      <div>Data: ${request.requestDate ? new Date(request.requestDate).toLocaleDateString('pt-BR') : '-'}</div>
      <div>Status: ${escapeHtml(statusLabel)}</div>
    </div>
  </div>

  <div class="info-grid">
    <div class="info-item">
      <label>CLIENTE</label>
      <span>${escapeHtml(request.clientName || 'N/A')}</span>
    </div>
    <div class="info-item">
      <label>DATA PREVISTA DE ENTREGA</label>
      <span>${request.expectedDeliveryDate ? new Date(request.expectedDeliveryDate).toLocaleDateString('pt-BR') : '-'}</span>
    </div>
    <div class="info-item">
      <label>MÉTODO DE ENVIO</label>
      <span>${shippingLabel ? escapeHtml(shippingLabel.icon + ' ' + shippingLabel.label) : '-'}</span>
    </div>
    <div class="info-item">
      <label>TRANSPORTADORA</label>
      <span>${escapeHtml(request.carrier || '-')}</span>
    </div>
    ${request.trackingCode ? `<div class="info-item"><label>CÓDIGO DE RASTREIO</label><span>${escapeHtml(request.trackingCode)}</span></div>` : ''}
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:35px;text-align:center;">#</th>
        <th>Código</th>
        <th>Projeto</th>
        <th>Tipo</th>
        <th style="width:55px;text-align:center;">Qtde</th>
        <th style="width:80px;text-align:right;">Preço Unit.</th>
        <th style="width:80px;text-align:center;">Peso</th>
        <th style="width:85px;text-align:right;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows}
      <tr class="totals">
        <td colspan="4" style="border:1px solid #ddd;padding:6px;text-align:right;">TOTAIS:</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:center;">${totalQuantity} un.</td>
        <td style="border:1px solid #ddd;padding:6px;"></td>
        <td style="border:1px solid #ddd;padding:6px;text-align:center;">${totalWeight.toFixed(2)} g</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">R$ ${totalValue.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  ${request.notes ? `<div style="margin-top:16px;"><strong>Observações:</strong><p style="margin-top:4px;padding:8px;background:#f5f5f5;">${escapeHtml(request.notes)}</p></div>` : ''}

  <div class="footer">
    <div>Criado por: ${escapeHtml(request.createdBy || 'N/A')} em ${request.createdAt ? new Date(request.createdAt).toLocaleString('pt-BR') : 'N/A'}</div>
    <div style="margin-top:4px;text-align:right;font-style:italic;">Documento gerado eletronicamente em ${new Date().toLocaleString('pt-BR')}</div>
  </div>

  <script>
    window.onload = function() { window.print(); setTimeout(function(){ window.close(); }, 500); };
  <\/script>
</body>
</html>`);
        printWindow.document.close();
    }, [addToast]);

    // ============================================
    // 3.5 HANDLERS
    // ============================================

    const handleClientChange = useCallback((clientId) => {
        setSelectedClientId(clientId);
        const client = clientOptions.find(c => c.id === clientId);
        if (client) {
            setClientName(client.razaoSocial);
        } else {
            setClientName('');
        }
        // Clear selected project and items when client changes
        setSelectedProjectId('');
        setRequestItems([]);
    }, [clientOptions]);

    const handleAddProject = useCallback(() => {
        if (!selectedProjectId) {
            setFormError('Selecione um projeto para adicionar.');
            return;
        }

        const project = projects?.find(p => p.id === selectedProjectId);
        if (!project) {
            setFormError('Projeto não encontrado.');
            return;
        }

        const quantity = parseFloat(newItemQuantity) || 0;
        if (quantity <= 0) {
            setFormError('Informe uma quantidade válida.');
            return;
        }

        const weight = parseFloat(newItemWeight) || 0;
        if (weight <= 0) {
            setFormError('Informe um peso válido.');
            return;
        }

        const unitPrice = parseFloat(newItemUnitPrice) || 0;

        const newItem = {
            id: crypto.randomUUID(),
            projectId: project.id,
            projectCode: project.code,
            projectName: project.projectName,
            productType: project.briefing?.productType || '',
            brandName: project.briefing?.brandName || '',
            quantity: quantity,
            unitPrice: unitPrice,
            total: quantity * unitPrice,
            weight: weight,
            weightUnit: newItemWeightUnit,
            notes: newItemNotes || '',
            // Include project details for reference
            netWeight: project.netWeight || '',
            grossWeight: project.grossWeight || '',
            density: project.density || ''
        };

        setRequestItems(prev => [...prev, newItem]);
        
        // Clear item form
        setSelectedProjectId('');
        setNewItemQuantity('1');
        setNewItemUnitPrice('0');
        setNewItemWeight('');
        setNewItemNotes('');
        setFormError('');
        
        addToast('Projeto adicionado à solicitação!', 'Success');
    }, [selectedProjectId, projects, newItemQuantity, newItemUnitPrice, newItemWeight, newItemWeightUnit, newItemNotes, addToast]);

    const handleRemoveItem = useCallback((itemId) => {
        setRequestItems(prev => prev.filter(item => item.id !== itemId));
    }, []);

    const handleUpdateItem = useCallback((itemId, field, value) => {
        setRequestItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const updated = { ...item, [field]: value };
                // Recalculate total if quantity or unitPrice changed
                if (field === 'quantity' || field === 'unitPrice') {
                    const qty = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
                    const price = parseFloat(field === 'unitPrice' ? value : item.unitPrice) || 0;
                    updated.total = qty * price;
                }
                return updated;
            }
            return item;
        }));
    }, []);

    const handleMoveItem = useCallback((index, direction) => {
        setRequestItems(prev => {
            const list = [...prev];
            if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) {
                return list;
            }
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            [list[index], list[newIndex]] = [list[newIndex], list[index]];
            return list;
        });
    }, []);

    const handleSave = useCallback(async (e) => {
        e.preventDefault();

        if (!editingItem && !can.create) {
            setFormError('Você não tem permissão para criar solicitações.');
            addToast('Você não tem permissão para criar solicitações.', 'Error');
            return;
        }
        if (editingItem && !can.edit) {
            setFormError('Você não tem permissão para editar solicitações.');
            addToast('Você não tem permissão para editar solicitações.', 'Error');
            return;
        }

        setFormError('');
        setFormSuccess('');

        // Validation
        if (!selectedClientId) {
            setFormError('Selecione um cliente.');
            return;
        }

        if (requestItems.length === 0) {
            setFormError('Adicione pelo menos um projeto à solicitação.');
            return;
        }

        setIsSubmitting(true);

        try {
            const code = editingItem?.code || generateNextCode();

            const requestData = {
                code,
                clientId: selectedClientId,
                clientName,
                requestDate,
                expectedDeliveryDate,
                shippingMethod,
                carrier,
                trackingCode,
                notes,
                items: requestItems,
                totalItems: requestItems.length,
                totalQuantity: requestItems.reduce((sum, item) => sum + (item.quantity || 0), 0),
                totalWeight: requestItems.reduce((sum, item) => sum + (item.weight || 0), 0),
                totalValue: requestItems.reduce((sum, item) => sum + (item.total || 0), 0),
                status: editingItem?.status || REQUEST_STATUS.DRAFT,
                updatedAt: new Date().toISOString()
            };

            let result;
            let requestId;

            if (editingItem) {
                result = await updateDocument(editingItem.id, requestData);
                if (result.success) {
                    setFormSuccess('Solicitação atualizada com sucesso!');
                    addToast('Solicitação atualizada com sucesso!', 'Success');
                    await notifySampleRequest(requestData, editingItem.id, true);
                    await logActivity(
                        'sample_request_updated',
                        `sample-request/${editingItem.id}`,
                        `Solicitação de amostra ${code} atualizada`
                    );
                    if (refreshRequests) await refreshRequests();
                    setTimeout(closeModal, 1500);
                } else {
                    setFormError(`Erro: ${result.error}`);
                    setIsSubmitting(false);
                }
            } else {
                requestData.createdAt = new Date().toISOString();
                requestData.createdBy = currentUser?.nome || 'Usuário';
                
                result = await addDocument(requestData);
                if (result.success) {
                    requestId = result.id;
                    setFormSuccess('Solicitação criada com sucesso!');
                    addToast('Solicitação criada com sucesso!', 'Success');
                    await notifySampleRequest(requestData, requestId, false);
                    await logActivity(
                        'sample_request_created',
                        `sample-request/${requestId}`,
                        `Solicitação de amostra ${code} criada para ${clientName}`
                    );
                    if (refreshRequests) await refreshRequests();
                    setTimeout(closeModal, 1500);
                } else {
                    setFormError(`Erro: ${result.error}`);
                    setIsSubmitting(false);
                }
            }
        } catch (error) {
            setFormError(`Erro ao salvar: ${error.message}`);
            setIsSubmitting(false);
        }
    }, [
        editingItem, can, selectedClientId, clientName, requestDate,
        expectedDeliveryDate, shippingMethod, carrier, trackingCode,
        notes, requestItems, generateNextCode, updateDocument,
        addDocument, notifySampleRequest, logActivity, closeModal,
        currentUser, refreshRequests, addToast
    ]);

    // --- CRUD Operations ---
    const handleNewClick = useCallback(() => {
        if (!can.create) {
            addToast('Você não tem permissão para criar solicitações.', 'Error');
            return;
        }
        resetForm();
        setIsModalOpen(true);
    }, [can.create, addToast, resetForm]);

    const handleEditClick = useCallback((item) => {
        if (!can.edit) {
            addToast('Você não tem permissão para editar solicitações.', 'Error');
            return;
        }

        setEditingItem(item);
        setSelectedClientId(item.clientId || '');
        setClientName(item.clientName || '');
        setRequestDate(item.requestDate || '');
        setExpectedDeliveryDate(item.expectedDeliveryDate || '');
        setShippingMethod(item.shippingMethod || 'sea');
        setCarrier(item.carrier || '');
        setTrackingCode(item.trackingCode || '');
        setNotes(item.notes || '');
        setRequestItems(item.items || []);
        setIsModalOpen(true);
    }, [can.edit, addToast]);

    const handleViewClick = useCallback((item) => {
        setSelectedRequest(item);
        setIsViewModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback(async (item) => {
        if (!can.delete) {
            addToast('Você não tem permissão para excluir solicitações.', 'Error');
            return;
        }

        const confirmed = window.confirm(`Tem certeza que deseja excluir a solicitação ${item.code}?`);
        if (!confirmed) return;

        setIsSubmitting(true);
        try {
            const result = await deleteDocument(item.id);
            if (result.success) {
                addToast('Solicitação excluída com sucesso!', 'Success');
                await logActivity(
                    'sample_request_deleted',
                    `sample-request/${item.id}`,
                    `Solicitação de amostra ${item.code} excluída`
                );
                if (refreshRequests) await refreshRequests();
            } else {
                addToast(`Erro: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Erro ao excluir solicitação.', 'Error');
        } finally {
            setIsSubmitting(false);
        }
    }, [can.delete, addToast, deleteDocument, logActivity, refreshRequests]);

    // --- Filters and Pagination ---
    const filteredRequests = useMemo(() => {
        if (!sampleRequests) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return sampleRequests.filter(item => {
            const matchesSearch = (
                (item.code && item.code.toLowerCase().includes(lowerSearch)) ||
                (item.clientName && item.clientName.toLowerCase().includes(lowerSearch))
            );
            const matchesStatus = !filterStatus || item.status === filterStatus;
            return matchesSearch && matchesStatus;
        }).sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateB - dateA;
        });
    }, [sampleRequests, searchTerm, filterStatus]);

    const totalPages = useMemo(() => Math.ceil(filteredRequests.length / ITEMS_PER_PAGE), [filteredRequests]);

    const currentRequests = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredRequests, currentPage]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            startTransition(() => {
                setCurrentPage(newPage);
            });
        }
    }, [totalPages]);

    const handleSearch = useCallback((value) => {
        startTransition(() => {
            setSearchTerm(value);
            setCurrentPage(1);
        });
    }, []);

    const handleFilterChange = useCallback((value) => {
        startTransition(() => {
            setFilterStatus(value);
            setCurrentPage(1);
        });
    }, []);

    const handleNavigateHome = useCallback(() => {
        window.location.href = '/dashboard';
    }, []);

    // ============================================
    // 4. RENDER MODAL
    // ============================================
    const renderRequestModal = () => {
        if (!isModalOpen) return null;

        return (
            <div className={styles.modalBackdrop} onClick={closeModal}>
                <div className={styles.formContainer} style={{ maxWidth: '1100px' }} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>{editingItem ? 'Editar Solicitação de Amostra' : 'Nova Solicitação de Amostra'}</h2>
                            {editingItem && (
                                <p className={styles.modalSubtitle}>
                                    Editando: <strong>{editingItem.code}</strong>
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={closeModal}
                            className={styles.modalCloseButton}
                            disabled={isSubmitting}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formError && (
                        <div className={`${styles.alert} ${styles.error}`}>
                            <span className={styles.alertIcon}><FiAlertCircle size={18} /></span>
                            {formError}
                        </div>
                    )}

                    {formSuccess && (
                        <div className={`${styles.alert} ${styles.success}`}>
                            <span className={styles.alertIcon}><FiCheck size={18} /></span>
                            {formSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSave} className={styles.formContent}>
                        {/* SECTION 1: CLIENT SELECTION */}
                        <FormSection title="Cliente" icon={<MdBusiness size={18} />} required>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Cliente <span className={styles.required}>*</span></label>
                                    <select
                                        value={selectedClientId}
                                        onChange={(e) => handleClientChange(e.target.value)}
                                        className={styles.select}
                                        disabled={isSubmitting || clientsLoading || !!editingItem}
                                        required
                                    >
                                        <option value="">Selecione um cliente...</option>
                                        {clientOptions.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.label}
                                            </option>
                                        ))}
                                    </select>
                                    <small className={styles.fieldHint}>
                                        Apenas clientes com projetos prontos para envio aparecem aqui
                                    </small>
                                    {clientOptions.length === 0 && !clientsLoading && (
                                        <small className={styles.fieldHint} style={{ color: '#856404' }}>
                                            ⚠️ Nenhum cliente possui projetos prontos para envio no momento.
                                        </small>
                                    )}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Data da Solicitação</label>
                                    <input
                                        type="date"
                                        value={requestDate}
                                        onChange={(e) => setRequestDate(e.target.value)}
                                        className={styles.input}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        </FormSection>

                        {/* SECTION 2: ADD PROJECTS */}
                        {selectedClientId && (
                            <FormSection 
                                title="Projetos Disponíveis para Envio" 
                                icon={<FaFlask size={18} />}
                                badge={`${availableProjectsForClient.length} disponíveis`}
                            >
                                {availableProjectsForClient.length === 0 ? (
                                    <div className={styles.infoAlert}>
                                        <span className={styles.infoIcon}><FiInfo size={18} /></span>
                                        <div className={styles.infoContent}>
                                            <p>
                                                {requestItems.length > 0 
                                                    ? 'Todos os projetos disponíveis já foram adicionados à solicitação.'
                                                    : 'Este cliente não possui projetos prontos para envio no momento.'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.twoColumnGrid}>
                                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                                <label>Selecionar Projeto <span className={styles.required}>*</span></label>
                                                <select
                                                    value={selectedProjectId}
                                                    onChange={(e) => setSelectedProjectId(e.target.value)}
                                                    className={styles.select}
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="">Selecione um projeto...</option>
                                                    {availableProjectsForClient.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.code} - {project.projectName}
                                                            {project.briefing?.productType && ` (${project.briefing.productType})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className={styles.inputGroup}>
                                                <label>Quantidade <span className={styles.required}>*</span></label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={newItemQuantity}
                                                    onChange={(e) => setNewItemQuantity(e.target.value)}
                                                    className={styles.input}
                                                    placeholder="Ex: 1"
                                                    disabled={isSubmitting || !selectedProjectId}
                                                />
                                            </div>

                                            <div className={styles.inputGroup}>
                                                <label>Preço Unitário (R$)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={newItemUnitPrice}
                                                    onChange={(e) => setNewItemUnitPrice(e.target.value)}
                                                    className={styles.input}
                                                    placeholder="0,00"
                                                    disabled={isSubmitting || !selectedProjectId}
                                                />
                                                <small className={styles.fieldHint}>
                                                    Deixe 0 para amostras gratuitas
                                                </small>
                                            </div>

                                            <div className={styles.inputGroup}>
                                                <label>Peso <span className={styles.required}>*</span></label>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={newItemWeight}
                                                        onChange={(e) => setNewItemWeight(e.target.value)}
                                                        className={styles.input}
                                                        placeholder="Ex: 250"
                                                        disabled={isSubmitting || !selectedProjectId}
                                                        style={{ flex: 1 }}
                                                    />
                                                    <select
                                                        value={newItemWeightUnit}
                                                        onChange={(e) => setNewItemWeightUnit(e.target.value)}
                                                        className={styles.select}
                                                        disabled={isSubmitting || !selectedProjectId}
                                                        style={{ width: '80px' }}
                                                    >
                                                        <option value="g">g</option>
                                                        <option value="kg">kg</option>
                                                        <option value="ml">ml</option>
                                                        <option value="L">L</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
                                            <label>Observações do Item</label>
                                            <input
                                                type="text"
                                                value={newItemNotes}
                                                onChange={(e) => setNewItemNotes(e.target.value)}
                                                className={styles.input}
                                                placeholder="Observações específicas para este item..."
                                                disabled={isSubmitting || !selectedProjectId}
                                            />
                                        </div>

                                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                type="button"
                                                onClick={handleAddProject}
                                                className={styles.primaryButton}
                                                disabled={isSubmitting || !selectedProjectId || !newItemQuantity || !newItemWeight}
                                            >
                                                <FiPlus size={18} /> Adicionar à Solicitação
                                            </button>
                                        </div>
                                    </>
                                )}
                            </FormSection>
                        )}

                        {/* SECTION 3: REQUEST ITEMS */}
                        <FormSection 
                            title="Itens da Solicitação" 
                            icon={<FiPackage size={18} />}
                            badge={`${requestItems.length} itens`}
                        >
                            {requestItems.length === 0 ? (
                                <div className={styles.emptyTable}>
                                    Nenhum item adicionado. Selecione um cliente e adicione projetos acima.
                                </div>
                            ) : (
                                <div className={styles.tableContainer}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '60px', textAlign: 'center' }}>#</th>
                                                <th>Código</th>
                                                <th>Projeto</th>
                                                <th>Tipo</th>
                                                <th style={{ width: '90px' }}>Qtde</th>
                                                <th style={{ width: '110px' }}>Preço Unit.</th>
                                                <th style={{ width: '100px' }}>Peso</th>
                                                <th style={{ width: '110px' }}>Total</th>
                                                <th style={{ width: '80px' }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requestItems.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div className={styles.orderControls}>
                                                            <button
                                                                type="button"
                                                                className={styles.orderButton}
                                                                onClick={() => handleMoveItem(index, 'up')}
                                                                disabled={index === 0 || isSubmitting}
                                                            >
                                                                <FiChevronUp size={12} />
                                                            </button>
                                                            <span style={{ fontWeight: 'bold', margin: '0 4px' }}>{index + 1}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.orderButton}
                                                                onClick={() => handleMoveItem(index, 'down')}
                                                                disabled={index === requestItems.length - 1 || isSubmitting}
                                                            >
                                                                <FiChevronDown size={12} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td><strong>{item.projectCode}</strong></td>
                                                    <td>{item.projectName}</td>
                                                    <td>{item.productType || '-'}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            value={item.quantity}
                                                            onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                                                            className={styles.input}
                                                            disabled={isSubmitting}
                                                            style={{ width: '70px', textAlign: 'center' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={item.unitPrice}
                                                            onChange={(e) => handleUpdateItem(item.id, 'unitPrice', e.target.value)}
                                                            className={styles.input}
                                                            disabled={isSubmitting}
                                                            style={{ width: '100px', textAlign: 'center' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={item.weight}
                                                                onChange={(e) => handleUpdateItem(item.id, 'weight', e.target.value)}
                                                                className={styles.input}
                                                                disabled={isSubmitting}
                                                                style={{ width: '70px', textAlign: 'center' }}
                                                            />
                                                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                                {item.weightUnit || 'g'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: 'bold' }}>
                                                        R$ {(item.total || 0).toFixed(2)}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={isSubmitting}
                                                            title="Remover"
                                                        >
                                                            <FiTrash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className={styles.tableFooterRow}>
                                                <td colSpan="4" className={styles.labelRight}>
                                                    <strong>Totais:</strong>
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                    {requestItems.reduce((sum, item) => sum + (item.quantity || 0), 0)} un.
                                                </td>
                                                <td></td>
                                                <td style={{ fontWeight: 'bold' }}>
                                                    {requestItems.reduce((sum, item) => sum + (item.weight || 0), 0).toFixed(2)} g
                                                </td>
                                                <td style={{ fontWeight: 'bold', color: 'var(--proj-primary-color)' }}>
                                                    R$ {requestItems.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2)}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </FormSection>

                        {/* SECTION 4: SHIPPING DETAILS */}
                        <FormSection title="Detalhes de Envio" icon={<FiTruck size={18} />}>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Data Prevista de Entrega</label>
                                    <input
                                        type="date"
                                        value={expectedDeliveryDate}
                                        onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                                        className={styles.input}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Método de Envio</label>
                                    <select
                                        value={shippingMethod}
                                        onChange={(e) => setShippingMethod(e.target.value)}
                                        className={styles.select}
                                        disabled={isSubmitting}
                                    >
                                        {SHIPPING_METHODS.map(method => (
                                            <option key={method.value} value={method.value}>
                                                {method.icon} {method.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Transportadora</label>
                                    <input
                                        type="text"
                                        value={carrier}
                                        onChange={(e) => setCarrier(e.target.value)}
                                        className={styles.input}
                                        placeholder="Ex: DHL, Fedex, UPS..."
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Código de Rastreio</label>
                                    <input
                                        type="text"
                                        value={trackingCode}
                                        onChange={(e) => setTrackingCode(e.target.value)}
                                        className={styles.input}
                                        placeholder="Código de rastreamento"
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>

                            <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
                                <label>Observações</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className={styles.textarea}
                                    rows={3}
                                    placeholder="Observações adicionais sobre a solicitação..."
                                    disabled={isSubmitting}
                                />
                            </div>
                        </FormSection>

                        {/* FORM ACTIONS */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={closeModal}
                                className={styles.secondaryButton}
                                disabled={isSubmitting}
                            >
                                <FiX size={18} /> Cancelar
                            </button>
                            <button
                                type="submit"
                                className={styles.primaryButton}
                                disabled={isSubmitting || requestItems.length === 0}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <FiSave size={18} />
                                        {editingItem ? 'Salvar Alterações' : 'Criar Solicitação'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // ============================================
    // 5. RENDER VIEW MODAL
    // ============================================
    const renderViewModal = () => {
        if (!isViewModalOpen || !selectedRequest) return null;

        return (
            <div className={styles.modalBackdrop} onClick={() => setIsViewModalOpen(false)}>
                <div className={styles.modalContainer} style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>Detalhes da Solicitação</h2>
                            <p className={styles.modalSubtitle}>
                                <strong>{selectedRequest.code}</strong> - {selectedRequest.clientName}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsViewModalOpen(false)}
                            className={styles.modalCloseButton}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        {/* Request Info */}
                        <div className={styles.productInfoCard}>
                            <div className={styles.productInfoGrid}>
                                <div className={styles.productInfoItem}>
                                    <label>Status</label>
                                    <div style={{ marginTop: '4px' }}>
                                        <RequestStatusBadge status={selectedRequest.status} />
                                    </div>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>Data da Solicitação</label>
                                    <span className={styles.productInfoValue}>
                                        {selectedRequest.requestDate ? new Date(selectedRequest.requestDate).toLocaleDateString('pt-BR') : '-'}
                                    </span>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>Data Prevista de Entrega</label>
                                    <span className={styles.productInfoValue}>
                                        {selectedRequest.expectedDeliveryDate ? new Date(selectedRequest.expectedDeliveryDate).toLocaleDateString('pt-BR') : '-'}
                                    </span>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>Método de Envio</label>
                                    <span className={styles.productInfoValue}>
                                        {SHIPPING_METHODS.find(m => m.value === selectedRequest.shippingMethod)?.icon}{' '}
                                        {SHIPPING_METHODS.find(m => m.value === selectedRequest.shippingMethod)?.label || selectedRequest.shippingMethod}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>
                            Itens da Solicitação ({selectedRequest.items?.length || 0})
                        </h4>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Código</th>
                                        <th>Projeto</th>
                                        <th>Tipo</th>
                                        <th>Qtde</th>
                                        <th>Preço Unit.</th>
                                        <th>Peso</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedRequest.items?.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td><strong>{item.projectCode}</strong></td>
                                            <td>{item.projectName}</td>
                                            <td>{item.productType || '-'}</td>
                                            <td>{item.quantity}</td>
                                            <td>R$ {(item.unitPrice || 0).toFixed(2)}</td>
                                            <td>{(item.weight || 0).toFixed(2)} {item.weightUnit || 'g'}</td>
                                            <td style={{ fontWeight: 'bold' }}>R$ {(item.total || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className={styles.tableFooterRow}>
                                        <td colSpan="4" className={styles.labelRight}>
                                            <strong>Totais:</strong>
                                        </td>
                                        <td>
                                            {selectedRequest.items?.reduce((sum, item) => sum + (item.quantity || 0), 0)} un.
                                        </td>
                                        <td></td>
                                        <td>
                                            {selectedRequest.items?.reduce((sum, item) => sum + (item.weight || 0), 0).toFixed(2)} g
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: 'var(--proj-primary-color)' }}>
                                            R$ {(selectedRequest.totalValue || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Notes */}
                        {selectedRequest.notes && (
                            <div style={{ marginTop: '24px' }}>
                                <h4 style={{ marginBottom: '8px' }}>Observações</h4>
                                <p style={{ 
                                    padding: '12px', 
                                    backgroundColor: 'var(--proj-bg-secondary)', 
                                    borderRadius: '4px',
                                    margin: 0
                                }}>
                                    {selectedRequest.notes}
                                </p>
                            </div>
                        )}

                        {/* Tracking Info */}
                        {(selectedRequest.carrier || selectedRequest.trackingCode) && (
                            <div style={{ marginTop: '24px' }}>
                                <h4 style={{ marginBottom: '8px' }}>Informações de Rastreio</h4>
                                <div style={{ 
                                    padding: '12px', 
                                    backgroundColor: 'var(--proj-bg-secondary)', 
                                    borderRadius: '4px'
                                }}>
                                    {selectedRequest.carrier && (
                                        <p style={{ margin: '0 0 4px 0' }}>
                                            <strong>Transportadora:</strong> {selectedRequest.carrier}
                                        </p>
                                    )}
                                    {selectedRequest.trackingCode && (
                                        <p style={{ margin: 0 }}>
                                            <strong>Código:</strong> {selectedRequest.trackingCode}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Metadata */}
                        <div style={{ 
                            marginTop: '24px', 
                            paddingTop: '16px', 
                            borderTop: '1px solid var(--proj-border-color)',
                            fontSize: '0.85rem',
                            color: 'var(--proj-text-muted)'
                        }}>
                            <p style={{ margin: 0 }}>
                                Criado por: {selectedRequest.createdBy || 'N/A'} em {
                                    selectedRequest.createdAt 
                                        ? new Date(selectedRequest.createdAt).toLocaleString('pt-BR') 
                                        : 'N/A'
                                }
                            </p>
                            {selectedRequest.updatedAt && (
                                <p style={{ margin: '4px 0 0 0' }}>
                                    Última atualização: {new Date(selectedRequest.updatedAt).toLocaleString('pt-BR')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={() => setIsViewModalOpen(false)}
                            className={styles.secondaryButton}
                        >
                            <FiX size={18} /> Fechar
                        </button>
                        <button
                            type="button"
                            onClick={() => handlePrintRequest(selectedRequest)}
                            className={styles.printButton}
                        >
                            <FiPrinter size={18} /> Imprimir
                        </button>
                        {can.edit && selectedRequest.status === REQUEST_STATUS.DRAFT && (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsViewModalOpen(false);
                                    handleEditClick(selectedRequest);
                                }}
                                className={styles.primaryButton}
                            >
                                <FiEdit size={18} /> Editar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // 6. MAIN RENDER
    // ============================================
    const showSkeleton = requestsLoading && !sampleRequests?.length;

    if (loadingTimeout) {
        return <LoadingOverlay onNavigateHome={handleNavigateHome} />;
    }

    if (showSkeleton) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}>
                        <div className={styles.spinner}></div>
                        <p>Carregando solicitações de amostras...</p>
                        <button onClick={handleNavigateHome} className={styles.navigationButton}>
                            <FiHome size={18} />
                            Ir para Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <div 
                            key={toast.id} 
                            className={`${styles.toast} ${styles[`toast${toast.type}`]}`}
                        >
                            {toast.type === 'Success' && <FiCheck size={18} />}
                            {toast.type === 'Error' && <FiAlertCircle size={18} />}
                            {toast.type === 'Info' && <FiInfo size={18} />}
                            {toast.message}
                            <button 
                                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                                className={styles.toastClose}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Modals */}
            {renderRequestModal()}
            {renderViewModal()}

            {/* Header */}
            <header className={styles.header}>
                <div>
                    <h1 className={styles.headerTitle}>Solicitações de Amostras</h1>
                    <p className={styles.headerSubtitle}>
                        Gerencie solicitações de amostras de projetos prontos para envio
                    </p>
                </div>
                {can.create && (
                    <button
                        type="button"
                        onClick={handleNewClick}
                        className={styles.addButton}
                        disabled={requestsLoading}
                    >
                        <FiPlus size={18} /> Nova Solicitação
                    </button>
                )}
            </header>

            {/* Search and Filters */}
            <div className={styles.searchBar}>
                <div className={styles.filterRow}>
                    <div className={styles.inputGroup} style={{ flex: 2 }}>
                        <label>Buscar</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Código, cliente..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                className={styles.input}
                                style={{ paddingLeft: '36px' }}
                                disabled={isPending}
                            />
                            <FiSearch size={18} style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--proj-text-secondary)'
                            }} />
                        </div>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label>Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className={styles.select}
                            disabled={isPending}
                        >
                            <option value="">Todos</option>
                            <option value={REQUEST_STATUS.DRAFT}>Rascunho</option>
                            <option value={REQUEST_STATUS.PENDING}>Pendente</option>
                            <option value={REQUEST_STATUS.APPROVED}>Aprovado</option>
                            <option value={REQUEST_STATUS.SENT}>Enviado</option>
                            <option value={REQUEST_STATUS.DELIVERED}>Entregue</option>
                            <option value={REQUEST_STATUS.CANCELLED}>Cancelado</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '120px' }}>Código</th>
                            <th>Cliente</th>
                            <th style={{ width: '110px' }}>Data</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>Itens</th>
                            <th style={{ width: '100px' }}>Peso Total</th>
                            <th style={{ width: '120px' }}>Valor Total</th>
                            <th style={{ width: '130px' }}>Status</th>
                            <th style={{ width: '200px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRequests.length === 0 ? (
                            <tr>
                                <td colSpan="8" className={styles.emptyTable}>
                                    {searchTerm || filterStatus
                                        ? 'Nenhuma solicitação encontrada com os filtros aplicados.'
                                        : 'Nenhuma solicitação de amostra encontrada. Clique em "Nova Solicitação" para criar.'}
                                </td>
                            </tr>
                        ) : (
                            currentRequests.map(request => (
                                <tr key={request.id} className={styles.tableRow}>
                                    <td><strong>{request.code}</strong></td>
                                    <td>{request.clientName}</td>
                                    <td>
                                        {request.requestDate
                                            ? new Date(request.requestDate).toLocaleDateString('pt-BR')
                                            : '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{request.items?.length || 0}</td>
                                    <td>{(request.totalWeight || 0).toFixed(2)} g</td>
                                    <td>R$ {(request.totalValue || 0).toFixed(2)}</td>
                                    <td><RequestStatusBadge status={request.status} /></td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.view && (
                                                <button
                                                    onClick={() => handleViewClick(request)}
                                                    className={`${styles.actionButton} ${styles.viewButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title="Visualizar"
                                                    type="button"
                                                >
                                                    <FiEye size={14} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handlePrintRequest(request)}
                                                className={`${styles.actionButton} ${styles.printButton}`}
                                                disabled={isSubmitting || isPending}
                                                title="Imprimir"
                                                type="button"
                                            >
                                                <FiPrinter size={14} />
                                            </button>
                                            {can.edit && request.status === REQUEST_STATUS.DRAFT && (
                                                <button
                                                    onClick={() => handleEditClick(request)}
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title="Editar"
                                                    type="button"
                                                >
                                                    <FiEdit size={14} />
                                                </button>
                                            )}
                                            {can.delete && request.status === REQUEST_STATUS.DRAFT && (
                                                <button
                                                    onClick={() => handleDeleteClick(request)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title="Excluir"
                                                    type="button"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className={styles.paginationControls}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isPending}
                        className={styles.paginationButton}
                    >
                        <FiArrowLeft size={16} /> Anterior
                    </button>
                    <span className={styles.paginationInfo}>
                        Página {currentPage} de {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || isPending}
                        className={styles.paginationButton}
                    >
                        Próxima <FiArrowRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default SampleRequestPage;